// ── Athena Profile Learning ───────────────────────────────────────────────────
// Observes behavioral patterns and detects drift from the original cognitive profile.
// NEVER alters the profile automatically — always prompts the user first.
// Writes to the same localStorage key ('athena_focus_profiles') used by the whole system.

import { trackBehavior } from './cognitiveProfile';

const PROFILES_KEY = 'athena_focus_profiles';

function readAll() {
  try { return JSON.parse(localStorage.getItem(PROFILES_KEY) || '[]'); } catch { return []; }
}
function writeAll(all) {
  try { localStorage.setItem(PROFILES_KEY, JSON.stringify(all)); } catch {}
}

// ── Message signal detection ──────────────────────────────────────────────────
const SIGNAL_RE = {
  VISUAL:         /\b(visual|diagrama|imagem|figura|ilustra[çc][aã]o|esquema|mapa mental|gr[aá]fico|desenhe?)\b/i,
  ESTRUTURADO:    /\b(passos?|etapas?|lista[s]?|organiz|passo a passo|numerado|checklist|dividid[ao]|estruturad[ao])\b/i,
  CONVERSACIONAL: /\b(conversa|me conta|o que você acha|sua opini[aã]o|fale mais|de forma simples|simplifica)\b/i,
  'PRÁTICO':      /\b(como fazer|pr[oó]ximo passo|pr[aá]tic[ao]|exerc[íi]cio|aplica[çc][aã]o|na pr[aá]tica|exemplo)\b/i,
};

// Maps signal trait → existing trackBehavior event key
const SIGNAL_TO_EVENT = {
  VISUAL:         'VISUAL_REQUEST',
  ESTRUTURADO:    'LONG_MESSAGE',    // repurpose: structure requests = "long-form" signal
  CONVERSACIONAL: 'SHORT_MESSAGE',   // repurpose: conversational = short/fluid
  'PRÁTICO':      'EXAMPLE_REQUEST',
};

export function trackMessageSignals(profileId, message) {
  if (!profileId || !message) return;
  const text = message.trim();
  if (text.length < 8) return;

  const matched = Object.entries(SIGNAL_RE)
    .filter(([, re]) => re.test(text))
    .map(([trait]) => trait);

  if (!matched.length) return;

  const all = readAll();
  const idx = all.findIndex(p => p.id === profileId);
  if (idx < 0 || !all[idx].cognitive) return;

  let updated = all[idx];
  for (const trait of matched) {
    const evt = SIGNAL_TO_EVENT[trait];
    if (evt) updated = trackBehavior(updated, evt);
  }
  all[idx] = updated;
  writeAll(all);
}

// ── Image open tracking ───────────────────────────────────────────────────────
export function trackImageOpened(profileId) {
  if (!profileId) return;
  const all = readAll();
  const idx = all.findIndex(p => p.id === profileId);
  if (idx < 0 || !all[idx].cognitive?.behavior) return;
  all[idx] = trackBehavior(all[idx], 'VISUAL_REQUEST');
  writeAll(all);
}

// ── Drift detection ───────────────────────────────────────────────────────────
// Returns { trait, message, delta } when significant drift found, else null.
export function checkDriftSuggestion(profile) {
  const b      = profile?.cognitive?.behavior;
  const scores = profile?.cognitive?.scores;
  if (!b || !scores || (b.sessions || 0) < 3) return null;

  const vis  = scores.VISUAL         || 0;
  const est  = scores.ESTRUTURADO    || 0;
  const conv = scores.CONVERSACIONAL || 0;
  const prat = scores['PRÁTICO']     || 0;

  if (vis < 6 && (b.visualRequests || 0) >= 4) return {
    trait:   'VISUAL',
    message: 'Percebi que você utiliza muitas referências visuais. Posso atualizar seu perfil para refletir isso?',
    delta:   +3,
  };

  if (est < 6 && (b.longMessages || 0) >= 5 && (b.sessions || 0) >= 4) return {
    trait:   'ESTRUTURADO',
    message: 'Percebi que você prefere conteúdo organizado em etapas. Posso ajustar seu perfil?',
    delta:   +3,
  };

  if (conv < 6 && (b.shortMessages || 0) >= 8) return {
    trait:   'CONVERSACIONAL',
    message: 'Percebi que você prefere um estilo mais conversacional. Posso atualizar seu perfil?',
    delta:   +3,
  };

  if (prat < 6 && (b.exampleRequests || 0) >= 4) return {
    trait:   'PRÁTICO',
    message: 'Percebi que você prefere exemplos práticos e aplicação direta. Posso ajustar seu perfil?',
    delta:   +3,
  };

  return null;
}

// ── Apply approved drift update ───────────────────────────────────────────────
export function applyDriftUpdate(profile, trait, delta) {
  if (!profile?.cognitive?.scores) return profile;
  const newScores = {
    ...profile.cognitive.scores,
    [trait]: (profile.cognitive.scores[trait] || 0) + delta,
  };
  // Re-sort traits by updated scores
  const newTraits = Object.entries(newScores)
    .sort(([, a], [, b]) => b - a)
    .map(([k]) => k);

  return {
    ...profile,
    cognitive: { ...profile.cognitive, scores: newScores, traits: newTraits },
  };
}
